import { useState, useEffect } from 'react';
import { Sun, Moon } from 'lucide-react';

interface ThemeToggleProps {
  className?: string;
  onToggle?: (isDark: boolean) => void;
  theme?: string;
}

export const ThemeToggle = ({ className = '', onToggle, theme }: ThemeToggleProps) => {
  const [isDark, setIsDark] = useState(false);

  // Force sync with document class
  const syncWithDocumentClass = () => {
    const hasDarkClass = document.documentElement.classList.contains('dark');
    if (isDark !== hasDarkClass) {
      console.log(`ThemeToggle: Synchronizing component state (${isDark}) with document class (${hasDarkClass})`);
      setIsDark(hasDarkClass);
    }
  };

  useEffect(() => {
    console.log("ThemeToggle useEffect triggered with theme prop:", theme);
    
    // Regardless of theme prop, ensure component state matches document class
    const hasDarkClass = document.documentElement.classList.contains('dark');
    
    if (theme) {
      const shouldBeDark = theme === 'dark';
      console.log("ThemeToggle: theme prop says isDark should be:", shouldBeDark, "(theme value is:", theme, ")");
      
      // Check for inconsistency between theme prop and document class
      if (shouldBeDark !== hasDarkClass) {
        console.log("WARNING: Theme prop and document class are inconsistent", 
          shouldBeDark ? "theme says dark but document has no dark class" : 
          "theme says light but document has dark class");
      }
      
      // Trust the theme prop over the document class
      setIsDark(shouldBeDark);
    } else {
      // No theme prop, trust the document class
      console.log("ThemeToggle: No theme prop, using document class isDark =", hasDarkClass);
      setIsDark(hasDarkClass);
    }
    
    // Debug current state
    console.log("Current document classes:", document.documentElement.className);
    
    // Add additional sync after a slight delay to catch any race conditions
    const timeoutId = setTimeout(syncWithDocumentClass, 50);
    return () => clearTimeout(timeoutId);
  }, [theme]);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    console.log("ThemeToggle: toggling theme, new isDark state:", newIsDark);
    
    // Update local state
    setIsDark(newIsDark);
    
    // Call parent handler if provided
    if (onToggle) {
      console.log("ThemeToggle: calling onToggle with:", newIsDark);
      onToggle(newIsDark);
    }
  };

  return (
    <button
      onClick={toggleTheme}
      className={`p-2 rounded-full bg-surface-light/80 dark:bg-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark hover:bg-primary-light/5 dark:hover:bg-primary-dark/5 transition-all duration-300 ${className}`}
      aria-label="Toggle theme"
    >
      {isDark ? (
        <Moon className="w-5 h-5 text-primary-dark" />
      ) : (
        <Sun className="w-5 h-5 text-primary-light" />
      )}
    </button>
  );
};

export default ThemeToggle; 